import { Button, Flex, Text } from '@chakra-ui/react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { RowClassParams } from 'ag-grid-community';

import type { LHCDetailInvoiceRowData } from '../../types';
import { getSapNoBiltoFromRow } from '../../utils/lhc-mutation-history';

const CHILD_ROW_BG = '#F1F1F1';

const LHC_TABLE_LINK_BLUE = '#0000A3';

interface POCellRendererContext {
  toggleExpand: (rowId: string) => void;
}

interface POCellRendererParams {
  value: string;
  data: LHCDetailInvoiceRowData;
  context?: POCellRendererContext;
}

export function POCellRenderer(params: POCellRendererParams) {
  if (params.data?.isChildRow) {
    if (!params.value) return null;
    return (
      <Flex align="center" h="100%">
        <Text fontSize="xs" color="gray.600">
          {params.value}
        </Text>
      </Flex>
    );
  }

  const isExpanded = params.data?.isExpanded;

  return (
    <Flex direction="column" justify="center" h="100%">
      <Text fontSize="sm" fontWeight={600}>
        {params.value}
      </Text>
      {params.data?.hasChildren && (
        <Button
          variant="link"
          size="sm"
          h="auto"
          minH="auto"
          p={0}
          justifyContent="flex-start"
          color="blue.700"
          fontSize="xs"
          fontWeight={500}
          textDecoration="none"
          _hover={{ textDecoration: 'none' }}
          _focusVisible={{ boxShadow: 'outline' }}
          onClick={(event) => {
            event.stopPropagation();
            if (params.data?.rowId && params.context?.toggleExpand) {
              params.context.toggleExpand(params.data.rowId);
            }
          }}
          rightIcon={
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 16,
                color: '#0000A3',
                transform: isExpanded ? 'rotate(180deg)' : undefined,
                transition: 'transform 0.2s',
              }}
            />
          }
        >
          Surat jalan lain
        </Button>
      )}
    </Flex>
  );
}

interface PaymentProofCellRendererParams {
  value: boolean;
  data: LHCDetailInvoiceRowData;
  onViewClick?: (row: LHCDetailInvoiceRowData) => void;
}

export function PaymentProofCellRenderer(
  params: PaymentProofCellRendererParams
) {
  if (
    params.data?.isChildRow ||
    !params.value ||
    !params.data?.paymentProofIds?.length
  ) {
    return '-';
  }

  return (
    <Flex align="center" justify="center" w="100%" h="100%">
      <Button
        variant="link"
        p={0}
        minH="auto"
        h="auto"
        color={LHC_TABLE_LINK_BLUE}
        fontWeight={400}
        fontSize="14px"
        lineHeight="20px"
        _focusVisible={{ boxShadow: 'outline' }}
        onClick={(event) => {
          event.stopPropagation();
          params.onViewClick?.(params.data);
        }}
      >
        Lihat
      </Button>
    </Flex>
  );
}

interface BankMutationNoCellRendererParams {
  value: string;
  data: LHCDetailInvoiceRowData;
  onSearchClick?: (row: LHCDetailInvoiceRowData) => void;
  onOpenMutationDetail?: (
    row: LHCDetailInvoiceRowData,
    bankMutationId: string
  ) => void;
}

export function BankMutationNoCellRenderer(
  params: BankMutationNoCellRendererParams
) {
  const { value, data, onSearchClick, onOpenMutationDetail } = params;

  if (data?.isChildRow) return value || '-';

  if (value === '-' && onSearchClick) {
    return (
      <Flex align="center" justify="center" w="100%" h="100%">
        <Button
          variant="link"
          p={0}
          minH="auto"
          h="auto"
          color={LHC_TABLE_LINK_BLUE}
          fontWeight={400}
          fontSize="14px"
          lineHeight="20px"
          _focusVisible={{ boxShadow: 'outline' }}
          onClick={(event) => {
            event.stopPropagation();
            onSearchClick(data);
          }}
        >
          Cari
        </Button>
      </Flex>
    );
  }

  if (data?.bankMutations?.length > 0 && onOpenMutationDetail) {
    return (
      <Flex direction="column" justify="center" h="100%" py={1}>
        {data.bankMutations.map((m) => (
          <Flex key={m.bankMutationId} align="center" gap={1}>
            <Text color={LHC_TABLE_LINK_BLUE} fontSize="14px" lineHeight="20px">
              &bull;
            </Text>
            <Button
              variant="link"
              p={0}
              minH="auto"
              h="auto"
              color={LHC_TABLE_LINK_BLUE}
              fontWeight={400}
              fontSize="14px"
              lineHeight="20px"
              whiteSpace="nowrap"
              _focusVisible={{ boxShadow: 'outline' }}
              onClick={(event) => {
                event.stopPropagation();
                onOpenMutationDetail(data, m.bankMutationId);
              }}
            >
              {m.bankMutationNo}
            </Button>
          </Flex>
        ))}
      </Flex>
    );
  }

  if (value && value !== '-') {
    return (
      <Flex align="center" h="100%">
        <Text color={LHC_TABLE_LINK_BLUE} fontSize="14px">
          {value}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex align="center" h="100%">
      <Text color={LHC_TABLE_LINK_BLUE} fontSize="14px">
        -
      </Text>
    </Flex>
  );
}

interface BillToCellRendererParams {
  value: string;
  data: LHCDetailInvoiceRowData;
  onViewMutationHistory?: (row: LHCDetailInvoiceRowData) => void;
}

export function BillToCellRenderer(params: BillToCellRendererParams) {
  const { value, data, onViewMutationHistory } = params;
  const display = value?.trim() ? value : '-';

  if (data?.isChildRow) {
    return (
      <Flex align="center" h="100%">
        <Text fontSize="xs" color="gray.600">
          {display}
        </Text>
      </Flex>
    );
  }

  const sapNoBilto = getSapNoBiltoFromRow(data);
  if (!sapNoBilto || !onViewMutationHistory) {
    return (
      <Flex align="center" h="100%">
        <Text fontSize="14px" color="#373F41">
          {display}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      h="100%"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Button
        variant="link"
        p={0}
        minH="auto"
        h="auto"
        color={LHC_TABLE_LINK_BLUE}
        fontWeight={400}
        fontSize="14px"
        lineHeight="20px"
        _focusVisible={{ boxShadow: 'outline' }}
        onClick={(event) => {
          event.stopPropagation();
          onViewMutationHistory(data);
        }}
      >
        {display}
      </Button>
    </Flex>
  );
}

interface DifferenceAmountCellRendererParams {
  value: string;
  data: LHCDetailInvoiceRowData;
}

export function DifferenceAmountCellRenderer(
  params: DifferenceAmountCellRendererParams
) {
  const { value, data } = params;

  if (data?.isChildRow || !value || value === '-') {
    return <span>{value || '-'}</span>;
  }

  const isNegativeFormatted = value.startsWith('-Rp');
  const isPositiveFormatted = value.startsWith('Rp') && !isNegativeFormatted;

  if (isNegativeFormatted) {
    const numberPart = value.replace('-Rp', '');
    return (
      <Text as="span" color="red.500" fontWeight={600}>
        ({numberPart})
      </Text>
    );
  }

  if (isPositiveFormatted) {
    const numberPart = value.replace('Rp', '');
    return (
      <Text as="span" color="gray.700" fontWeight={600}>
        {numberPart}
      </Text>
    );
  }

  return <span>{value}</span>;
}

export function getRowStyle(params: RowClassParams<LHCDetailInvoiceRowData>) {
  if (params.data?.isChildRow) {
    return { background: CHILD_ROW_BG };
  }

  return undefined;
}
